import type { CSSProperties } from 'react';
import {
  LAYOUT_TEMPLATES,
  type Layout,
  type LayoutTemplate,
  type LayoutTemplateDef,
  type LayoutZoneDef,
} from '@ghostscreen/contracts';
import { i18n } from '../../i18n';

export { LAYOUT_TEMPLATES };
export type { LayoutTemplate, LayoutTemplateDef, LayoutZoneDef };

export const TEMPLATE_ORDER: LayoutTemplate[] = ['full', 'split', 'side', 'ticker'];

export function templateDef(template: LayoutTemplate): LayoutTemplateDef {
  return LAYOUT_TEMPLATES[template];
}

/** Zone keys are stored ids ("Main", "Left", …); "Left" is the physical left of the screen. */
const ZONE_NAME_KEYS = {
  Main: 'main',
  Left: 'left',
  Right: 'right',
  Side: 'side',
  Ticker: 'ticker',
} as const;

export function zoneName(key: string): string {
  const id = ZONE_NAME_KEYS[key as keyof typeof ZONE_NAME_KEYS];
  return id ? i18n.t(`layouts:zones.${id}`) : key;
}

export function layoutSummary(
  layout: Layout,
  playlistName: (playlistId: string | null) => string | null,
): string {
  const def = templateDef(layout.template);
  return def.zones
    .filter((zone) => !zone.ticker)
    .map((zone) => {
      const assigned = layout.zones.find((item) => item.key === zone.key)?.playlistId ?? null;
      return i18n.t('layouts:summary.zone', {
        zone: zoneName(zone.key),
        playlist: playlistName(assigned) || i18n.t('layouts:summary.empty'),
      });
    })
    .join(' · ');
}

export function gridStyle(def: LayoutTemplateDef): CSSProperties {
  return {
    display: 'grid',
    gap: 4,
    width: '100%',
    aspectRatio: '16 / 9',
    boxSizing: 'border-box',
    padding: 4,
    background: '#0b0b0d',
    borderRadius: 10,
    gridTemplateColumns: def.cols,
    gridTemplateRows: def.rows,
  };
}

export function zoneStyle(zone: LayoutZoneDef, big = false): CSSProperties {
  return {
    gridArea: zone.area,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    borderRadius: 6,
    display: 'grid',
    placeItems: 'center',
    padding: '4px 8px',
    fontSize: big ? 12.5 : 10.5,
    fontWeight: 700,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: zone.ticker ? '#FFFFFF' : 'var(--gs-ink2)',
    background: zone.ticker ? 'var(--gs-accent-solid)' : 'var(--gs-panel2)',
  };
}
